import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { sub } from 'date-fns'

import SendListTable from '../components/sendList/SendListTable';

import { getManualTranslation } from '../i18n';

import { apiList, getCancelData } from '../api/callAPI';

import useUser from '../hook/useUser';
import useCancel from '../hook/useCancel';
import useGlobalStatus from '../hook/useGlobalStatus';
import useReportStatus from '../hook/useReportStatus';
import useApiError from '../hook/useApiError';

import * as basicConstants from '../constants/basic';
import * as alertConstants from '../constants/alertMsg';
import * as urlConstants from '../constants/url';
import * as mediaConstants from '../constants/mediaData';

import SendEmailModal from '../components/sendList/SendEmailModal';
import { SEND_ALL_ERROR_MSG, cleanSearchKeyword, checkAddressesIsNull } from '../components/formData/formData';
import AlertMsg from '../components/alert/AlertMsg';
import { MODAL_SEND_LIST } from '../components/modal/constants';
import { handleFormattedMessage } from '../components/formData/formData';

import { reportModeMapping, initSendAllEmailsSettings } from '../components/sendList/data/sendDetailConstants';

import { formatDate2, formatDate3, lastMonthFistDate, lastMonthlastDate, last7day, last14day, yesterday } from '../utils/date';


const SendList = ({ media }) => {
  const history = useHistory();
  const intl = useIntl();
  const { userData, setUserData, setAuthStatusMsg, setAlertMsg } = useUser();
  const { currentActiveData, setCurrentActiveData, locale, setLocale, entryPath, setEntryPath, setModalContent, isModalOpen, setIsModalOpen, setModalData } = useGlobalStatus();
  const { cancelData, setCancelData } = useCancel();
  const { handleApiErrorStatus } = useApiError();
  const [modalName, setModalName] = useState('');
  const [reportDateRange, setReportDateRange] = useState({ startReportDate: null, endReportDate: null });
  const [reportMode, setReportMode] = useState("");
  const [downloadInputErrorMsg, setDownloadInputErrorMsg] = useState(SEND_ALL_ERROR_MSG)
  const {
    searchKeyword,
    setSearchKeyword,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    sendList,
    setSendList,
    sendListLength,
    setSendListLength,
    setCurrentTab,
    setIsLoading,
    setIsProcessing
  } = useReportStatus();

  const fetchData = (cancelToken) => {
    setIsLoading(true);
    let result;

    if (searchKeyword) {
      result = apiList[mediaConstants.MEDIA_DATA[media].api.reportList]({
        page: page,
        size: rowsPerPage,
        sort: orderBy,
        order: order,
        searchKeyword: cleanSearchKeyword(searchKeyword)
      }, cancelToken)
    } else {
      result = apiList[mediaConstants.MEDIA_DATA[media].api.reportList]({
        page: page,
        size: rowsPerPage,
        sort: orderBy,
        order: order
      }, cancelToken)
    }

    result.then(res => {
      if (res.message && res.message === alertConstants.CANCEL_REQUEST) return;
      if (res.status === 200) {
        const resResult = res.data.result;
        setSendList(resResult.content);
        setSendListLength(resResult.totalElements);
        setIsLoading(false);
      } else {
        handleApiErrorStatus(res);
      }
    })
  }

  useEffect(() => {
    if (media !== mediaConstants.GOOGLE && media !== mediaConstants.FACEBOOK) {
      setLocale("zh-TW");
    }
  }, [media])

  useEffect(() => {
    if (!userData) return;

    setIsLoading(true);

    if (cancelData) {
      cancelData.cancel(alertConstants.CANCEL_REQUEST);
    }

    const call = getCancelData();
    setCancelData(call);

    if (searchKeyword) {
      const timer = setTimeout(() => {
        fetchDataWithToken(call.token);
      }, 500);

      return () => {
        clearTimeout(timer);
        call.cancel(alertConstants.CANCEL_REQUEST);
      };
    } else {
      fetchDataWithToken(call.token);
    }

    return () => {
      call.cancel(alertConstants.CANCEL_REQUEST);
    };
  }, [userData, page, order, orderBy, rowsPerPage, searchKeyword, media]);

  const fetchDataWithToken = (token) => {
    fetchData(token);
  }

  useEffect(() => {
    if (entryPath === '') {
      history.push(urlConstants.PAGE_MEDIA_MENU);
    }
  }, [entryPath])

  useEffect(() => {
    const { reportMode, reportDateRange } = initSendAllEmailsSettings(currentActiveData.reportType);
    setReportMode(reportMode);
    setReportDateRange(reportDateRange);
  }, [currentActiveData.reportType]);

  const deleteReport = (deleteData) => {
    setIsProcessing(true);
    apiList[mediaConstants.MEDIA_DATA[media].api.deleteReport](deleteData.weeklyReportId)
      .then(res => {
        if (res.status === 200) {
          setIsProcessing(false);
          fetchData();
        } else if (res.status === 401) {
          setAuthStatusMsg(alertConstants.ERROR_CERTIFICATE);
          setUserData(false);
          history.push(urlConstants.PAGE_LOGIN);
        } else {
          setAlertMsg(alertConstants.ERROR_SYSTEMCONNECT);
        }
        setIsModalOpen(false);
      })
  }

  const testDownloadOrSend = (e) => {
    const { name } = e.target;
    const requestData = { weeklyReportId: currentActiveData.weeklyReportId }

    if (name === 'testSend') {
      setIsProcessing('testSend');

      apiList[mediaConstants.MEDIA_DATA[media].api.testSend](requestData)
        .then(res => {
          if (res.status === 200) {
            setIsProcessing(false);
            setModalContent({ ...MODAL_SEND_LIST['completeSend'], media: media });
            setIsModalOpen(true);
            setTimeout(() => {
              setIsModalOpen(false);
            }, 2000)
          } else {
            handleApiErrorStatus(res);
            setIsProcessing(false);
          }
        })
    }

    if (name === 'testDownload') {
      setIsProcessing('testDownload');

      const currentMedia = media.toLowerCase();
      const reportMedia = getManualTranslation(`common.title.${currentMedia}Report`, currentActiveData.language);

      let startDate = "";
      let endDate = "";

      if (currentActiveData.reportType === 1) {
        startDate = formatDate3(lastMonthFistDate);
        endDate = formatDate3(lastMonthlastDate);
      } else if (currentActiveData.reportType === 3) {
        startDate = formatDate3(last14day);
        endDate = formatDate3(yesterday);
      } else {
        startDate = formatDate3(last7day);
        endDate = formatDate3(yesterday);
      }

      const fileName = `${reportMedia}${currentActiveData.weeklyReportName}_${startDate}-${endDate}.xlsx`;

      apiList[mediaConstants.MEDIA_DATA[media].api.testDownload](requestData)
        .then(res => {
          if (res.status === 201) {
            const contentType = res.headers['content-type'];
            const file = new Blob([res.data], { type: contentType });
            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);
            setIsProcessing(false);
          } else {
            handleApiErrorStatus(res);
            setIsProcessing(false);
          }
        })
    }
  }

  const handleSendAllEmails = () => {
    if (checkAddressesIsNull(currentActiveData.toAddresses)) {
      setModalContent({ ...MODAL_SEND_LIST['checkNoEmail'], media: media });
      setIsModalOpen(true);
    } else {
      setModalName('edit_send_all_emails');
    }
  }

  const fetchSendAllEmails = () => {
    setModalName("");
    setIsProcessing('sendAllEmails');

    const updateReportMode = reportModeMapping[reportMode] ?? 3;

    const requestData = {
      weeklyReportId: currentActiveData.weeklyReportId,
      start_date: formatDate2(reportDateRange.startReportDate),
      end_date: formatDate2(reportDateRange.endReportDate),
      toAddresses: currentActiveData.toAddresses.join(", "),
      report_mode: updateReportMode
    }

    apiList[mediaConstants.MEDIA_DATA[media].api.sendAllEmails](requestData)
      .then(res => {
        if (res.status === 200) {
          setIsProcessing(false);
          setModalContent({ ...MODAL_SEND_LIST['completeSendAllEmails'], media: media });
          setIsModalOpen(true);
          setTimeout(() => {
            setIsModalOpen(false);
          }, 2000)
        } else {
          handleApiErrorStatus(res);
          setIsProcessing(false);
        }
      })
  }

  const handleModalSubmit = (data, type) => {
    switch (type) {
      case 'delete_report':
        deleteReport(data);
        break;
      case 'check_no_email':
        setEntryPath(mediaConstants.MEDIA_DATA[media].editDataUrl);
        history.push(mediaConstants.MEDIA_DATA[media].createDataUrl);
        setIsModalOpen(false);
        break;
      default:
        break;
    }
  }

  // console.log('sendList', sendList)

  return (
    <>
      {userData.scopes.includes(basicConstants.AUTH_EDIT_REPORT) &&
        <SendListTable
          media={media}
          fetchData={fetchData}
          testDownloadOrSend={testDownloadOrSend}
          handleSendAllEmails={handleSendAllEmails}
          handleModalSubmit={handleModalSubmit}
        />
      }
      {modalName === 'edit_send_all_emails' &&
        <SendEmailModal
          media={media}
          open={modalName === 'edit_send_all_emails'}
          handleClose={() => {
            setModalName(false)
            setDownloadInputErrorMsg(SEND_ALL_ERROR_MSG);
          }}
          reportDateRange={reportDateRange}
          setReportDateRange={setReportDateRange}
          reportMode={reportMode}
          setReportMode={setReportMode}
          fetchSendAllEmails={fetchSendAllEmails}
          downloadInputErrorMsg={downloadInputErrorMsg}
          setDownloadInputErrorMsg={setDownloadInputErrorMsg}
        />
      }
      <AlertMsg />
    </>
  );
}

export default SendList;